import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { PageHero } from "@/components/page-hero";
import { blogPosts } from "@/content/blog";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { cn } from "@/lib/utils";
import { ALL_AUTHORS } from "@/content/authors";

type InsightsSearch = { category?: string; topic?: string };

export const Route = createFileRoute("/hr-insights/")({
  validateSearch: (search: Record<string, unknown>): InsightsSearch => ({
    category: typeof search.category === "string" && search.category ? search.category : undefined,
    topic: typeof search.topic === "string" && search.topic ? search.topic : undefined,
  }),
  head: () => ({
    meta: [
      { title: "HR & Employment Law Blog for UK Employers" },
      {
        name: "description",
        content: "Expert UK HR and employment law guidance from CIPD-qualified advisors and SRA-regulated solicitors.",
      },
      { property: "og:title", content: "HR & Employment Law Blog for UK Employers" },
      { property: "og:description", content: "Plain-English guidance for UK employers." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/hr-insights" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/hr-insights" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "EmployerHQ HR & Employment Law Blog",
          url: "https://www.hrservicecentre.co.uk/hr-insights",
          description:
            "Expert UK HR and employment law guidance from CIPD-qualified advisors and SRA-regulated solicitors.",
          publisher: { "@id": "https://www.hrservicecentre.co.uk/#organization" },
          blogPost: blogPosts.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            description: p.excerpt,
            url: `https://www.hrservicecentre.co.uk/hr-insights/${p.slug}`,
            author: {
              "@type": "Person",
              name: p.author,
              ...(ALL_AUTHORS.find((author) => author.name === p.author)
                ? {
                    url: `https://www.hrservicecentre.co.uk/about-employerhq#author-${ALL_AUTHORS.find((author) => author.name === p.author)?.slug}`,
                  }
                : {}),
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Resources", item: "https://www.hrservicecentre.co.uk/hr-resources" },
            { "@type": "ListItem", position: 3, name: "Blog", item: "https://www.hrservicecentre.co.uk/hr-insights" },
          ],
        }),
      },
    ],
  }),
  component: BlogIndex,
});

const categories = Array.from(new Set(blogPosts.map((p) => p.category))).sort();

const topics = (() => {
  const counts = new Map<string, number>();
  for (const p of blogPosts) {
    for (const k of new Set((p.keywords ?? []).map((k) => k.trim().toLowerCase()))) {
      if (k.length < 4 || k.split(" ").length > 4) continue;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .filter(([, n]) => n > 1)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 12)
    .map(([k]) => k);
})();

function BlogIndex() {
  const { category, topic } = Route.useSearch();
  const navigate = useNavigate({ from: "/hr-insights/" });

  const setFilter = (next: InsightsSearch) =>
    navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, ...next }), replace: true });

  const posts = useMemo(
    () =>
      blogPosts.filter((p) => {
        if (category && p.category !== category) return false;
        if (topic) {
          const haystack = [p.title, p.excerpt, ...(p.keywords ?? [])].join(" ").toLowerCase();
          if (!haystack.includes(topic.toLowerCase())) return false;
        }
        return true;
      }),
    [category, topic],
  );

  const chip = (active: boolean) =>
    cn(
      "rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider ring-1 transition",
      active
        ? "bg-brand text-primary-foreground ring-brand"
        : "bg-card text-muted-foreground ring-black/10 hover:text-brand",
    );

  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Resources", path: "/hr-resources" },
          { label: "Insights", path: "/hr-insights" },
        ]}
        eyebrow="Insights"
        title="HR & Employment Law Blog for UK Employers"
        subtitle="The EmployerHQ Journal, HR and employment law insights for UK employers."
        intro="Plain-English HR and employment law writing for UK business owners and HR leads. Every post is reviewed by a qualified advisor before publication."
      />

      <section className="px-6 pt-12 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Browse by category
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className={chip(!category)} onClick={() => setFilter({ category: undefined })}>
              All updates
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                className={chip(category === c)}
                onClick={() => setFilter({ category: category === c ? undefined : c })}
              >
                {c}
              </button>
            ))}
          </div>

          {topics.length > 0 && (
            <>
              <h2 className="mt-8 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Browse by topic
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {topics.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={chip(topic === t)}
                    onClick={() => setFilter({ topic: topic === t ? undefined : t })}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="mt-8 flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              Showing {posts.length} of {blogPosts.length} articles
            </span>
            {(category || topic) && (
              <button
                type="button"
                className="font-semibold text-accent underline underline-offset-4"
                onClick={() => setFilter({ category: undefined, topic: undefined })}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="px-6 py-12 lg:px-8 lg:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 lg:grid-cols-3">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to="/hr-insights/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col rounded-2xl bg-card p-8 ring-1 ring-black/5 transition hover:-translate-y-0.5"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-accent">{p.category}</div>
              <h2 className="mt-3 font-serif text-xl leading-snug text-brand">{p.title}</h2>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
              <div className="mt-6 text-xs text-muted-foreground">
                {p.readingTime} min · {p.author}
              </div>
            </Link>
          ))}
          {posts.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No articles match that combination yet. Try another category or topic.
            </p>
          )}
        </div>
      </section>


      <InternalLinksHub
        heading="Everything UK employers need from an HR partner"
        intro="Browse our most-requested services, sector pages and free resources, all written by senior HR and employment law specialists."
      />
    </>
  );
}
